import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  orders as ordersApi, partners as partnersApi, payments as paymentsApi,
  services as servicesApi,
} from '../api/endpoints';
import { useOrderEvents } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import {
  Loading, Modal, Can, StatusBadge, money, dateTime, dateOnly,
} from '../components/Common';

export default function OrderDetail() {
  const { id } = useParams();
  const toast = useToast();
  const { can } = useAuth();

  const [order, setOrder] = useState(null);
  const [statusFlow, setStatusFlow] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await ordersApi.detail(id);
      setOrder(res.data.order);
      setStatusFlow(res.data.statusFlow);
    } catch (err) {
      toast.error('Could not load the order', err.message);
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  // Refresh when this particular order changes anywhere else in the system.
  useOrderEvents((event, payload) => {
    if (payload?._id === id || payload?.orderId === id) load();
  });

  const setStatus = async (status) => {
    try {
      await ordersApi.setStatus(id, { status });
      toast.success(`Moved to ${status.replace(/-/g, ' ')}`);
      load();
    } catch (err) {
      toast.error('Could not update status', err.message);
    }
  };

  const makeTags = async () => {
    try {
      await ordersApi.tags(id);
      toast.success('Garment tags generated');
      load();
    } catch (err) {
      toast.error('Could not generate tags', err.message);
    }
  };

  const openInvoice = async () => {
    try {
      const res = await ordersApi.invoice(id);
      setInvoice(res.data.invoice);
      setInvoiceOpen(true);
    } catch (err) {
      toast.error('Could not load the invoice', err.message);
    }
  };

  /**
   * Publishing the price.
   *
   * An app order carries no money the customer can see until this is pressed -
   * they placed a request, and the bill is counted off the garments that
   * actually turned up. Confirming reveals the total in their app, notifies
   * them, and opens the pay-online button.
   */
  const confirmBill = async () => {
    setConfirming(true);
    try {
      await ordersApi.confirmBill(id);
      toast.success('Bill confirmed', 'The customer can now see the price and pay online.');
      load();
    } catch (err) {
      toast.error('Could not confirm the bill', err.message);
    } finally {
      setConfirming(false);
    }
  };

  const collectCash = async () => {
    try {
      await paymentsApi.collect({ orderId: id });
      toast.success('Payment recorded');
      load();
    } catch (err) {
      toast.error('Could not record the payment', err.message);
    }
  };

  if (loading) return <Loading label="Loading order" />;
  if (!order) return null;

  const doneIndex = statusFlow.indexOf(order.status);

  const billed = order.billStatus === 'confirmed';
  // The bill is counted off garments we are holding, so it cannot be published
  // before the pickup is done. Walk-ins arrive already confirmed.
  const collected = ['picked-up', 'at-store', 'in-process', 'ready', 'out-for-delivery', 'delivered']
    .includes(order.status);
  const editable = !['delivered', 'cancelled'].includes(order.status);

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/orders" className="muted" style={{ fontSize: 13 }}>← Back to orders</Link>
          <h1 style={{ marginTop: 6 }}>
            {order.orderNumber} <StatusBadge status={order.status} />
            {' '}
            <span className={`badge badge-${billed ? 'green' : 'amber'}`}>
              {billed ? 'Bill confirmed' : 'Bill pending'}
            </span>
          </h1>
          <div className="sub">
            Placed {dateTime(order.createdAt)}
            {order.isExpress ? ' · Express service' : ''}
            {order.store?.name ? ` · ${order.store.name}` : ''}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Can permission="order.view">
            <button className="btn btn-ghost" onClick={openInvoice}>Invoice</button>
          </Can>
          <Can permission="order.update">
            <button className="btn btn-ghost" onClick={makeTags}>Generate tags</button>
          </Can>
          <Can permission="order.update">
            <button className="btn btn-ghost" onClick={() => setItemsOpen(true)} disabled={!editable}>
              Edit items
            </button>
          </Can>
          <Can permission={['dispatch.update', 'order.update']}>
            <button className="btn btn-blue" onClick={() => setAssignOpen(true)}>Assign partner</button>
          </Can>
        </div>
      </div>

      <div className="grid grid-2">
        <div>
          <div className="card mb">
            <div className="card-head">
              <h3>Items</h3>
              {order.serviceName ? (
                <span className="badge badge-blue">{order.serviceName}</span>
              ) : null}
            </div>

            {/* An app order is booked against a service, not a garment list -
                this is empty until someone counts the bag. */}
            {order.items.length === 0 ? (
              <div className="card-body">
                <div className="strong">Nothing counted yet</div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
                  {order.customerName || 'The customer'} booked{' '}
                  <strong>{order.serviceName || 'a pickup'}</strong>. Open{' '}
                  <strong>Edit items</strong> to record what actually arrived, then
                  confirm the bill.
                </div>
              </div>
            ) : null}

            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr><th>Service</th><th className="center">Qty</th><th className="right">Rate</th><th className="right">Total</th></tr>
                </thead>
                <tbody>
                  {order.items.map((line, i) => (
                    <tr key={i}>
                      <td>
                        {line.name}
                        <div className="muted" style={{ fontSize: 12 }}>{line.categoryName} · per {line.unit}</div>
                        {line.tags?.length ? (
                          <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {line.tags.map((t) => <span key={t} className="badge badge-gray mono">{t}</span>)}
                          </div>
                        ) : null}
                      </td>
                      <td className="center">{line.quantity}</td>
                      <td className="right">{money(line.price)}</td>
                      <td className="right strong">{money(line.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card-body" style={{ borderTop: '1px solid var(--line)' }}>
              <Row label="Subtotal" value={money(order.subtotal)} />
              {order.expressCharge > 0 ? <Row label="Express charge" value={money(order.expressCharge)} /> : null}
              {order.deliveryCharge > 0 ? <Row label="Delivery" value={money(order.deliveryCharge)} /> : null}
              {order.discount > 0 ? <Row label={`Discount (${order.couponCode})`} value={`− ${money(order.discount)}`} tone="green" /> : null}
              {/* The taxes and fees this order was actually billed with. */}
              {(order.charges?.length
                ? order.charges
                : [{ name: 'GST', amount: order.gstAmount }]
              ).map((c, i) => (
                <Row
                  key={i}
                  label={`${c.name}${c.type === 'percent' ? ` (${c.value}%)` : ''}`}
                  value={money(c.amount)}
                />
              ))}
              <div style={{ borderTop: '1px solid var(--line)', marginTop: 9, paddingTop: 9 }}>
                <Row label="Total" value={money(order.total)} big />
                {/* Points are 1:1 with rupees and already counted inside Paid. */}
                {order.pointsUsed > 0 ? (
                  <Row
                    label={`Urban Wallet (${order.pointsUsed} points)`}
                    value={`− ${money(order.pointsUsed)}`}
                    tone="green"
                  />
                ) : null}
                <Row label="Paid" value={money(order.amountPaid)} tone={order.amountPaid >= order.total ? 'green' : 'amber'} />
                {order.amountPaid < order.total ? (
                  <Row label="Outstanding" value={money(order.total - order.amountPaid)} tone="amber" />
                ) : null}
              </div>

              {/* The customer sees none of the above until this is pressed. */}
              {!billed ? (
                <Can permission="order.update">
                  <div className="bill-gate mt">
                    <div className="strong" style={{ color: 'var(--amber)' }}>
                      The customer cannot see this price yet
                    </div>
                    <div className="muted" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
                      {collected
                        ? 'Check the items above match what you counted, then confirm. The total appears in their app straight away, along with a pay-online button.'
                        : 'Confirm the pickup first — the bill is counted from the items collected.'}
                    </div>
                    <button
                      className="btn btn-primary btn-block mt"
                      onClick={confirmBill}
                      disabled={confirming || !collected}
                    >
                      {confirming ? 'Confirming…' : `Confirm bill & notify customer · ${money(order.total)}`}
                    </button>
                  </div>
                </Can>
              ) : null}

              {billed && order.paymentStatus !== 'paid' && can('payment.create') ? (
                <button className="btn btn-primary btn-block mt" onClick={collectCash}>
                  Record cash payment · {money(order.total - order.amountPaid)}
                </button>
              ) : null}
            </div>
          </div>

          <div className="card">
            <div className="card-head"><h3>Move this order</h3></div>
            <div className="card-body">
              <Can
                permission="order.update"
                fallback={<div className="muted">You do not have permission to change the status.</div>}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {statusFlow.filter((s) => s !== 'cancelled').map((s, i) => (
                    <button
                      key={s}
                      className={`btn btn-sm ${i === doneIndex ? 'btn-primary' : 'btn-ghost'}`}
                      disabled={i === doneIndex || order.status === 'delivered'}
                      onClick={() => setStatus(s)}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {s.replace(/-/g, ' ')}
                    </button>
                  ))}
                </div>
              </Can>
            </div>
          </div>
        </div>

        <div>
          <div className="card mb">
            <div className="card-head"><h3>Customer &amp; pickup</h3></div>
            <div className="card-body">
              <Field label="Customer" value={order.customerName || order.user?.name || '—'} />
              <Field label="Mobile" value={order.customerMobile || order.user?.mobile} />
              <Field label="Pickup" value={`${dateOnly(order.pickupDate)} · ${order.pickupSlot || '—'}`} />
              {order.deliveryDate ? <Field label="Delivery" value={`${dateOnly(order.deliveryDate)} · ${order.deliverySlot || '—'}`} /> : null}
              {order.pickupAddress ? (
                <Field
                  label="Pickup address"
                  value={[order.pickupAddress.houseNo, order.pickupAddress.line1, order.pickupAddress.landmark,
                          order.pickupAddress.city, order.pickupAddress.pincode].filter(Boolean).join(', ')}
                />
              ) : null}
              {order.specialInstructions ? <Field label="Instructions" value={order.specialInstructions} /> : null}

              <div className="row mt">
                <div>
                  <div className="muted" style={{ fontSize: 11.5, fontWeight: 700 }}>PICKUP OTP</div>
                  <div className="mono strong" style={{ fontSize: 19, color: 'var(--navy)' }}>{order.pickupOtp || '—'}</div>
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 11.5, fontWeight: 700 }}>DELIVERY OTP</div>
                  <div className="mono strong" style={{ fontSize: 19, color: 'var(--navy)' }}>{order.deliveryOtp || '—'}</div>
                </div>
              </div>
            </div>
          </div>

          {order.deliveryPartner ? (
            <div className="card mb">
              <div className="card-head"><h3>Delivery partner</h3></div>
              <div className="card-body">
                <Field label="Name" value={order.deliveryPartner.name || '—'} />
                <Field label="Mobile" value={order.deliveryPartner.mobile} />
                <Field label="Vehicle" value={`${order.deliveryPartner.vehicle?.type || ''} ${order.deliveryPartner.vehicle?.number || ''}`.trim() || '—'} />
                {order.deliveryPartner.currentLocation?.lat ? (
                  <Field
                    label="Last seen"
                    value={`${order.deliveryPartner.currentLocation.lat.toFixed(4)}, ${order.deliveryPartner.currentLocation.lng.toFixed(4)}`}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="card">
            <div className="card-head"><h3>Timeline</h3></div>
            <div className="card-body">
              <div className="timeline">
                {[...order.timeline].reverse().map((entry, i) => (
                  <div key={i} className="tl-item done">
                    <div className="tl-dot" />
                    <div className="tl-label">{entry.label || entry.status}</div>
                    <div className="tl-meta">
                      {dateTime(entry.at)} · {entry.byModel}
                      {entry.note ? ` — ${entry.note}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditItemsModal
        open={itemsOpen}
        order={order}
        onClose={() => setItemsOpen(false)}
        onSaved={() => { setItemsOpen(false); load(); }}
      />

      <AssignModal
        open={assignOpen}
        orderId={id}
        onClose={() => setAssignOpen(false)}
        onAssigned={() => { setAssignOpen(false); load(); }}
      />

      <Modal open={invoiceOpen} title="GST Invoice" onClose={() => setInvoiceOpen(false)} wide
        footer={<button className="btn btn-primary" onClick={() => window.print()}>Print</button>}>
        {invoice ? <InvoiceView invoice={invoice} /> : null}
      </Modal>
    </>
  );
}

function Row({ label, value, tone, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span className={big ? 'strong' : 'muted'} style={{ fontSize: big ? 15 : 13 }}>{label}</span>
      <span className="strong" style={{ fontSize: big ? 17 : 13.5, color: tone === 'green' ? 'var(--green)' : tone === 'amber' ? 'var(--amber)' : 'var(--navy)' }}>
        {value}
      </span>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 11 }}>
      <div className="muted" style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px' }}>{label}</div>
      <div style={{ fontSize: 13.5, marginTop: 2 }}>{value || '—'}</div>
    </div>
  );
}

/**
 * Counting the order.
 *
 * What the customer picked in the app was a guess at what they were sending;
 * this is where the store records what actually arrived. Lines that came out
 * of a package are shown but not editable - they are derived from the package
 * and are re-generated server-side, so touching them here would double-count.
 */
function EditItemsModal({ open, order, onClose, onSaved }) {
  const toast = useToast();
  const [catalogue, setCatalogue] = useState([]);
  const [lines, setLines] = useState([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setLines(
      (order.items || [])
        .filter((line) => !line.fromPackage)
        .map((line) => ({
          item: line.item,
          name: line.name,
          unit: line.unit,
          price: line.price,
          quantity: line.quantity,
        }))
    );
    servicesApi.items({ isActive: true })
      .then((res) => setCatalogue(res.data.items))
      .catch(() => toast.error('Could not load the service list'));
  }, [open, order, toast]);

  const setQuantity = (itemId, quantity) => {
    setLines((list) => (quantity <= 0
      ? list.filter((l) => String(l.item) !== String(itemId))
      : list.map((l) => (String(l.item) === String(itemId) ? { ...l, quantity } : l))));
  };

  const addItem = (doc) => {
    setLines((list) => {
      const existing = list.find((l) => String(l.item) === String(doc._id));
      if (existing) {
        return list.map((l) => (String(l.item) === String(doc._id) ? { ...l, quantity: l.quantity + 1 } : l));
      }
      return [...list, { item: doc._id, name: doc.name, unit: doc.unit, price: doc.price, quantity: 1 }];
    });
  };

  const query = search.trim().toLowerCase();
  const matches = query
    ? catalogue.filter((doc) => doc.name.toLowerCase().includes(query)).slice(0, 8)
    : [];

  const subtotal = lines.reduce((sum, l) => sum + l.price * l.quantity, 0);
  const fromPackages = (order.items || []).filter((line) => line.fromPackage);

  const submit = async () => {
    if (!lines.length && !(order.packages || []).length) {
      return toast.error('Add at least one item');
    }
    setBusy(true);
    try {
      await ordersApi.updateItems(order._id, {
        items: lines.map((l) => ({ item: l.item, quantity: l.quantity })),
      });
      toast.success('Items updated', 'Confirm the bill when the count is final.');
      onSaved();
    } catch (err) {
      toast.error('Could not update the items', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} title={`Count items · ${order.orderNumber}`} onClose={onClose} wide
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : 'Save items'}
        </button>
      </>}>
      <div className="field">
        <label>Add a service</label>
        <input
          className="input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search shirt, saree, blanket…"
        />
        {matches.length ? (
          <div className="picker">
            {matches.map((doc) => (
              <button key={doc._id} type="button" className="picker-row" onClick={() => addItem(doc)}>
                <span>
                  {doc.name}
                  <span className="muted" style={{ fontSize: 12 }}> · {doc.category?.name} · per {doc.unit}</span>
                </span>
                <span className="strong">{money(doc.price)}</span>
              </button>
            ))}
          </div>
        ) : null}
        {query && !matches.length ? <div className="hint">Nothing matched "{search}".</div> : null}
      </div>

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Service</th><th className="center" style={{ width: 130 }}>Qty</th>
              <th className="right">Rate</th><th className="right">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan={4} className="muted">Nothing counted yet — search above to add the items you received.</td></tr>
            ) : lines.map((line) => (
              <tr key={String(line.item)}>
                <td>
                  {line.name}
                  <div className="muted" style={{ fontSize: 12 }}>per {line.unit}</div>
                </td>
                <td className="center">
                  <div className="stepper">
                    <button type="button" onClick={() => setQuantity(line.item, line.quantity - 1)}>−</button>
                    <input
                      value={line.quantity}
                      onChange={(e) => setQuantity(line.item, parseInt(e.target.value, 10) || 0)}
                      inputMode="numeric"
                    />
                    <button type="button" onClick={() => setQuantity(line.item, line.quantity + 1)}>+</button>
                  </div>
                </td>
                <td className="right">{money(line.price)}</td>
                <td className="right strong">{money(line.price * line.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {fromPackages.length ? (
        <div className="hint mt">
          Also included from packages, priced with the bundle:{' '}
          {fromPackages.map((l) => `${l.name} × ${l.quantity}`).join(', ')}
        </div>
      ) : null}

      <div className="mt" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span className="strong">Item total</span>
        <span className="strong" style={{ color: 'var(--navy)' }}>{money(subtotal)}</span>
      </div>
      <div className="hint">
        Delivery, taxes and any coupon are recalculated by the server when you save.
      </div>
    </Modal>
  );
}

function AssignModal({ open, orderId, onClose, onAssigned }) {
  const toast = useToast();
  const [list, setList] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [leg, setLeg] = useState('pickup');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Only approved riders can take a job.
    partnersApi.list({ kycStatus: 'approved', limit: 100 })
      .then((res) => setList(res.data.partners))
      .catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!partnerId) return toast.error('Pick a delivery partner');
    setBusy(true);
    try {
      await ordersApi.assign(orderId, { partnerId, leg });
      toast.success('Order assigned');
      onAssigned();
    } catch (err) {
      toast.error('Could not assign', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} title="Assign a delivery partner" onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Assigning…' : 'Assign'}</button>
      </>}>
      <div className="field">
        <label>Leg</label>
        <select className="select" value={leg} onChange={(e) => setLeg(e.target.value)}>
          <option value="pickup">Pickup</option>
          <option value="delivery">Delivery</option>
        </select>
      </div>
      <div className="field">
        <label>Partner</label>
        <select className="select" value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
          <option value="">Select a partner…</option>
          {list.map((p) => (
            <option key={p._id} value={p._id}>
              {p.name || p.mobile} {p.isOnline ? '· online' : '· offline'} ({p.employmentType})
            </option>
          ))}
        </select>
        {list.length === 0 ? <div className="hint">No approved partners yet — approve one under Delivery Partners.</div> : null}
      </div>
    </Modal>
  );
}

function InvoiceView({ invoice }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div className="strong" style={{ fontSize: 16, color: 'var(--navy)' }}>{invoice.seller.name}</div>
          <div className="muted" style={{ fontSize: 12.5, maxWidth: 260, marginTop: 3 }}>{invoice.seller.address}</div>
          {invoice.seller.gstNumber ? <div className="muted" style={{ fontSize: 12.5 }}>GSTIN: {invoice.seller.gstNumber}</div> : null}
        </div>
        <div className="right">
          <div className="strong mono">{invoice.invoiceNumber}</div>
          <div className="muted" style={{ fontSize: 12.5 }}>{dateOnly(invoice.invoiceDate)}</div>
        </div>
      </div>

      <div className="mb">
        <div className="muted" style={{ fontSize: 11.5, fontWeight: 700 }}>BILL TO</div>
        <div className="strong">{invoice.buyer.name}</div>
        <div className="muted" style={{ fontSize: 12.5 }}>{invoice.buyer.mobile}</div>
      </div>

      <table className="data">
        <thead><tr><th>Service</th><th className="center">Qty</th><th className="right">Rate</th><th className="right">Amount</th></tr></thead>
        <tbody>
          {invoice.items.map((l, i) => (
            <tr key={i}><td>{l.name}</td><td className="center">{l.quantity}</td>
              <td className="right">{money(l.price)}</td><td className="right">{money(l.total)}</td></tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 14, marginLeft: 'auto', maxWidth: 260 }}>
        <Row label="Subtotal" value={money(invoice.subtotal)} />
        {invoice.expressCharge > 0 ? <Row label="Express" value={money(invoice.expressCharge)} /> : null}
        {invoice.discount > 0 ? <Row label="Discount" value={`− ${money(invoice.discount)}`} tone="green" /> : null}
        {(invoice.charges?.length
          ? invoice.charges
          : [{ name: 'GST', amount: invoice.gstAmount }]
        ).map((c, i) => (
          <Row key={i} label={`${c.name}${c.type === 'percent' ? ` @ ${c.value}%` : ''}`} value={money(c.amount)} />
        ))}
        <Row label="Total" value={money(invoice.total)} big />
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          {invoice.paymentMode?.toUpperCase()} · {invoice.paymentStatus}
        </div>
      </div>
    </div>
  );
}
